package fr.manyverse.app.domain.usecase

import fr.manyverse.app.domain.image.ImageOptimizer
import fr.manyverse.app.domain.model.EventContent
import fr.manyverse.app.domain.model.EventType
import fr.manyverse.app.domain.model.GameEvent
import fr.manyverse.app.domain.model.ImageData
import fr.manyverse.app.domain.repository.GameEventRepository
import fr.manyverse.app.domain.repository.ImageStorageRepository
import fr.manyverse.app.domain.validation.AnnouncementValidator
import fr.manyverse.app.domain.validation.ImageValidator

/**
 * Use case for creating an announcement event.
 * Validates the content, validates + optimizes the image (resize + WebP) when present,
 * uploads it through the shared image pipeline, then creates the event.
 */
class CreateAnnouncementUseCase(
    private val gameEventRepository: GameEventRepository,
    private val imageStorageRepository: ImageStorageRepository,
    private val imageOptimizer: ImageOptimizer
) {
    suspend operator fun invoke(
        tableId: String,
        triggeredBy: String,
        targetParticipants: List<String>,
        text: String?,
        image: ImageData?
    ): Result<GameEvent> {
        AnnouncementValidator.validateForCreation(
            text = text,
            imageBytes = image?.bytes,
            participants = targetParticipants
        ).onFailure {
            return Result.failure(it)
        }

        var imageUrl: String? = null
        if (image != null) {
            ImageValidator.validate(image).onFailure { return Result.failure(it) }

            val optimized = imageOptimizer.optimize(image).getOrElse { return Result.failure(it) }

            imageUrl = imageStorageRepository.uploadAnnouncementImage(tableId, optimized)
                .getOrElse { return Result.failure(it) }
        }

        val content = EventContent.Announcement(
            text = text?.trim()?.takeIf { it.isNotBlank() },
            imageUrl = imageUrl
        )

        return gameEventRepository.createEvent(
            tableId = tableId,
            type = EventType.ANNOUNCEMENT,
            triggeredBy = triggeredBy,
            targetParticipants = targetParticipants,
            content = content
        )
    }
}
